import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Cards from '@cloudscape-design/components/cards';
import Icon, { IconProps } from '@cloudscape-design/components/icon';
import Alert from '@cloudscape-design/components/alert';
import Link from '@cloudscape-design/components/link';

interface Tool {
  id: string;
  name: string;
  description: string | React.ReactNode;
  icon: string;
}

interface LandingPageProps {
  onSelectTool: (toolId: string) => void;
}

const LandingPage = ({ onSelectTool }: LandingPageProps) => {
  const tools: Tool[] = [
    {
      id: 'invisible-character-legend',
      name: 'Invisible Character Legend',
      description: 'Browse and reference all invisible characters, control characters, and special Unicode characters. Includes Unicode codes, names, and categories for educational and reference purposes.',
      icon: 'search',
    },
    {
      id: 'invisible-characters',
      name: 'Check text on invisible Characters',
      description: (
        <>
          Detect, analyze, and remove hidden characters from text. Identify security risks like homoglyphs, zero-width characters, and steganography patterns. <strong>Also useful for identifying fingerprints from GenAI outputs (e.g., ChatGPT, Claude, etc.).</strong>
        </>
      ),
      icon: 'view-full',
    },
    {
      id: 'eml-msg-analyzer',
      name: 'Check EMail EML-File on invisible Characters',
      description: 'Upload and comprehensively analyze email files in EML format. Includes header analysis, authentication checks, and invisible character detection.',
      icon: 'upload',
    },
    {
      id: 'url-safety-checker',
      name: 'Check URL on invisible Characters',
      description: 'Detect and analyze invisible characters hidden in URLs. Identifies zero-width characters, directional marks, and other hidden Unicode that can be used to disguise malicious links or bypass security filters.',
      icon: 'security',
    },
    {
      id: 'safe-links-decoder',
      name: 'M365 Safe Link Decoder',
      description: 'Decode Microsoft 365 Safe Links URLs to reveal the original destination. Supports batch processing and provides detailed URL analysis.',
      icon: 'unlocked',
    },
    {
      id: 'mail-header-analyzer',
      name: 'Mail Header Analysis',
      description: 'Analyze email headers to trace delivery paths, verify authentication (SPF/DKIM/DMARC), and identify potential security issues.',
      icon: 'envelope',
    },
  ];

  return (
    <SpaceBetween size="l">
      <Container>
        <SpaceBetween size="m">
          <Box variant="p">
            Unicode Security Analyzer is a comprehensive suite of utilities designed to help users detect and analyze invisible characters in text, emails, and URLs. Invisible characters can be used for various purposes including steganography, fingerprinting AI-generated content, phishing attacks, and security exploits. Each tool is built with modern best practices and follows AWS Cloudscape design patterns for a consistent user experience. Select a tool below to begin.
          </Box>

          <Alert type="info" header="100% Data Privacy">
            <Box variant="p">
              • No data transmission to external servers<br />
              • All processing performed exclusively on your device
            </Box>
          </Alert>

          <Alert type="warning" header="Disclaimer - Use at Your Own Risk">
            <Box variant="p">
              Unicode Security Analyzer is provided <strong>free of charge</strong> and is intended for informational and analysis purposes only. 
              While all data processing happens locally on your device and no data is transmitted or modified externally, 
              <strong> we provide no guarantees or warranties of any kind</strong> regarding the accuracy, completeness, or reliability of the analysis results. 
              By using these tools, you acknowledge that you do so <strong>at your own risk</strong> and that the application assumes <strong>no responsibility or liability</strong> for 
              any decisions made or actions taken based on the analysis output. Always verify critical findings through multiple sources and consult with security professionals for production environments.
            </Box>
          </Alert>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Choose a tool to get started"
          >
            Available Tools
          </Header>
        }
      >
        <Cards
          cardDefinition={{
            header: (item) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: '#0972d3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name={item.icon as IconProps.Name} size="normal" variant="inverted" />
                </div>
                <Link 
                  variant="primary" 
                  fontSize="heading-m"
                  onFollow={(e) => {
                    e.preventDefault();
                    onSelectTool(item.id);
                  }}
                >
                  {item.name}
                </Link>
              </div>
            ),
            sections: [
              {
                id: 'description',
                content: (item) => (
                  <Box variant="p" color="text-body-secondary" fontSize="body-s">
                    {item.description}
                  </Box>
                ),
              },
            ],
          }}
          items={tools}
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 500, cards: 2 },
            { minWidth: 800, cards: 3 },
            { minWidth: 1200, cards: 4 },
          ]}
          onSelectionChange={({ detail }) => {
            if (detail.selectedItems.length > 0) {
              onSelectTool(detail.selectedItems[0].id);
            }
          }}
          selectionType="single"
        />
      </Container>
    </SpaceBetween>
  );
};

export default LandingPage;

